import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { useSocket } from './hooks/useSocket'
import { Sidebar } from './components/layout/Sidebar'
import { Header } from './components/layout/Header'
import { NotificationToastHost } from './components/layout/NotificationToastHost'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import MyIssuesPage from './pages/MyIssuesPage'
import AccountingPage from './pages/AccountingPage'
import ProjectDetailPage from './pages/ProjectDetailPage'
import BoardPage from './pages/BoardPage'
import IssuePage from './pages/IssuePage'
import WorkloadPage from './pages/WorkloadPage'
import EmployeeLogsDashboardPage from './pages/EmployeeLogsDashboardPage'
import PeoplePerformancePage from './pages/PeoplePerformancePage'
import ProjectValuesPage from './pages/ProjectValuesPage'
import LeaveManagementPage from './pages/LeaveManagementPage'
import AttendancePage from './pages/AttendancePage'
import ManagerHomePage from './pages/ManagerHomePage'
import ProjectSettingsPage from './pages/ProjectSettingsPage'
import UsersPage from './pages/admin/UsersPage'
import { useAuthStore } from './store/authStore'
import { canEditUsers, canViewReports, hasFullAccess } from './lib/roles'

function HomeRedirect() {
  const { user } = useAuthStore()
  return <Navigate to={canViewReports(user?.role) ? '/manager-home' : '/my-issues'} replace />
}

function DashboardRoute() {
  const { user } = useAuthStore()
  if (!canViewReports(user?.role)) return <Navigate to="/my-issues" replace />
  return <WorkloadPage />
}

function AccountingRoute() {
  const { user } = useAuthStore()
  const canAccessAccounting = !!user?.canSubmitExpenses
  return canAccessAccounting ? <AccountingPage /> : <Navigate to="/my-issues" replace />
}

function ProtectedLayout({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Header />
        <NotificationToastHost />
        <main style={{ flex: 1, overflowY: 'auto', background: '#f8fafc' }}>
          {children}
        </main>
      </div>
    </div>
  )
}

export default function App() {
  const { isLoading, isAuthenticated } = useAuth()
  const { user } = useAuthStore()
  useSocket()

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, sans-serif',
          background: '#f8fafc',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: '3px solid #e2e8f0',
            borderTopColor: '#6366f1',
            borderRadius: '50%',
            animation: 'spin 0.7s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <ProtectedLayout>
      <Routes>
        <Route path="/" element={<HomeRedirect />} />
        <Route path="/manager-home" element={canViewReports(user?.role) ? <ManagerHomePage /> : <Navigate to="/my-issues" replace />} />
        <Route path="/my-issues" element={canViewReports(user?.role) ? <Navigate to="/manager-home" replace /> : <MyIssuesPage />} />
        <Route path="/leaves" element={user?.role === 'CLIENT' ? <Navigate to="/my-issues" replace /> : <LeaveManagementPage />} />
        <Route path="/attendance" element={user?.role === 'CLIENT' ? <Navigate to="/my-issues" replace /> : <AttendancePage />} />
        <Route path="/accounting" element={<AccountingRoute />} />
        <Route path="/projects/:projectId" element={<BoardPage />} />
        <Route path="/projects/:projectId/list" element={<ProjectDetailPage />} />
        <Route path="/projects/:projectId/board" element={<BoardPage />} />
        <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />
        <Route path="/issues/:issueId" element={<IssuePage />} />
        <Route path="/dashboard" element={<DashboardRoute />} />
        <Route
          path="/dashboard-2"
          element={canViewReports(user?.role) ? <EmployeeLogsDashboardPage /> : <Navigate to="/my-issues" replace />}
        />
        <Route
          path="/project-values"
          element={hasFullAccess(user?.role) ? <ProjectValuesPage /> : <Navigate to="/my-issues" replace />}
        />
        <Route
          path="/people-performance"
          element={canViewReports(user?.role)
            ? <PeoplePerformancePage />
            : <Navigate to="/my-issues" replace />}
        />
        <Route
          path="/workload"
          element={canViewReports(user?.role) ? <Navigate to="/dashboard" replace /> : <Navigate to="/my-issues" replace />}
        />
        <Route path="/admin/users" element={canEditUsers(user?.role) ? <UsersPage /> : <Navigate to="/my-issues" replace />} />
        <Route path="*" element={<Navigate to="/my-issues" replace />} />
      </Routes>
    </ProtectedLayout>
  )
}
